import {
  AVATARS,
  AVATAR_BACKGROUNDS,
  REWARD_FRAMES,
  type Avatar,
  type CutSticker,
  type Decor,
  type Swatch,
  defaultAvatar,
} from '@shkola/design';
import { type CSSProperties, type ReactNode, useCallback, useEffect, useState } from 'react';

const byName = (files: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(files).map(([path, url]) => [path.slice(path.lastIndexOf('/') + 1, -5), url]),
  );

// the same 3D stickers as the app (Fluent Emoji, MIT), bundled by Vite
const cut = byName(
  import.meta.glob<string>('../../../../packages/design/stickers/cut/*.webp', {
    eager: true,
    query: '?url',
    import: 'default',
  }),
);
const plain = byName(
  import.meta.glob<string>('../../../../packages/design/stickers/*.webp', {
    eager: true,
    query: '?url',
    import: 'default',
  }),
);

/** The address of a 3D sticker (the board draws them as pictures). */
export const cutStickerUrl = (name: string): string | undefined => cut[name];

export function Sticker({
  name,
  size = 48,
  rotate = 0,
  className = '',
  style,
}: {
  name: CutSticker;
  size?: number;
  rotate?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <img
      src={cut[name]}
      alt=""
      aria-hidden
      draggable={false}
      width={size}
      height={size}
      className={`pointer-events-none select-none ${className}`}
      style={{ rotate: `${rotate}deg`, ...style }}
    />
  );
}

export function DecorImage({
  name,
  size,
  rotate = 0,
  blur = 0,
  className = '',
  style,
}: {
  name: Decor | Avatar;
  size: number;
  rotate?: number;
  blur?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <img
      src={plain[name]}
      alt=""
      aria-hidden
      draggable={false}
      width={size}
      height={size}
      className={`pointer-events-none select-none ${className}`}
      style={{ rotate: `${rotate}deg`, filter: blur ? `blur(${blur}px)` : undefined, ...style }}
    />
  );
}

export function AvatarCircle({
  avatar,
  size,
  frame,
}: {
  avatar: Avatar;
  size: number;
  /** a frame from «Магазин нагород»: a coloured ring around the white edge */
  frame?: string | null;
}) {
  const ring = frame ? REWARD_FRAMES[frame] : undefined;
  if (ring) {
    return (
      <span
        className="inline-flex shrink-0 rounded-full"
        style={{ padding: Math.max(3, Math.round(size / 20)), background: ring }}
      >
        <AvatarCircle avatar={avatar} size={size} />
      </span>
    );
  }
  const [from, to] = AVATAR_BACKGROUNDS[avatar];
  return (
    <span
      className="flex items-center justify-center overflow-hidden rounded-full border-white"
      style={{
        width: size,
        height: size,
        borderWidth: Math.max(3, Math.round(size / 26)),
        backgroundImage: `radial-gradient(circle at 35% 30%, ${from} 0%, ${to} 100%)`,
      }}
    >
      <DecorImage name={avatar} size={Math.round(size * 0.68)} />
    </span>
  );
}

/** The first letters of a name in a grey circle: the adults' face in the menu (no photos). */
export function InitialsCircle({ name, size }: { name: string; size: number }) {
  const initials = name
    .split(/[\s-]+/)
    .filter((w) => /^[\p{L}\p{N}]/u.test(w))
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-fill font-display font-semibold text-ink"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
    >
      {initials || '•'}
    </span>
  );
}

const avatarKey = (memberId: string) => `shkola.avatar.${memberId}`;
const AVATAR_EVENT = 'shkola:avatar';

function readAvatar(memberId: string): Avatar {
  try {
    const saved = localStorage.getItem(avatarKey(memberId));
    if (saved && (AVATARS as ReadonlyArray<string>).includes(saved)) return saved as Avatar;
  } catch {
    // private mode: the default animal
  }
  return defaultAvatar(memberId);
}

/** The student's animal, kept in this browser (no photo of a child anywhere). */
export function useAvatar(memberId: string) {
  const [avatar, setAvatar] = useState<Avatar>(() => readAvatar(memberId));
  useEffect(() => {
    const sync = () => setAvatar(readAvatar(memberId));
    window.addEventListener(AVATAR_EVENT, sync);
    return () => window.removeEventListener(AVATAR_EVENT, sync);
  }, [memberId]);
  const choose = useCallback(
    (next: Avatar) => {
      try {
        localStorage.setItem(avatarKey(memberId), next);
      } catch {
        // not remembered, still shown until reload
      }
      setAvatar(next);
      window.dispatchEvent(new Event(AVATAR_EVENT));
    },
    [memberId],
  );
  return { avatar, choose };
}

/** A sticker that pops in with a spring and then drifts on its own rhythm. */
export function Floating({
  children,
  delay = 0,
  className = '',
  style,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={`motion-pop absolute ${className}`}
      style={{ '--delay': `${0.15 + delay * 0.09}s`, ...style } as CSSProperties}
    >
      <div
        className="motion-float"
        style={
          {
            '--duration': `${2.8 + delay * 0.4}s`,
            '--delay': `${delay * 0.25}s`,
            '--float': '-5px',
          } as CSSProperties
        }
      >
        {children}
      </div>
    </div>
  );
}

/** Soft cloud puffs drifting slowly over the sky; `blur` melts them into the sky. */
export function Clouds({
  puffs,
  duration,
  blur = 0,
}: {
  puffs: ReadonlyArray<[string, string, number, number, number?]>;
  duration: number;
  blur?: number;
}) {
  return (
    <div
      className="motion-drift pointer-events-none absolute inset-0"
      style={
        {
          '--duration': `${duration}s`,
          filter: blur ? `blur(${blur}px)` : undefined,
        } as CSSProperties
      }
    >
      {puffs.map(([left, top, w, h, opacity = 1], i) => (
        <span
          key={i}
          className="absolute"
          style={{
            left,
            top,
            width: w,
            height: h,
            opacity,
            backgroundImage:
              'radial-gradient(closest-side, rgb(255 255 255 / 0.96) 0%, rgb(255 255 255 / 0.72) 48%, rgb(255 255 255 / 0) 100%)',
          }}
        />
      ))}
    </div>
  );
}

/**
 * A glass folder: a light back with its tab, a few sheets of the section peeking out (`sheets`, their
 * colours) and a frosted front with the section's sticker on it. The sheets rise a little on hover.
 */
export function FolderArt({
  sticker,
  sheets,
  note,
}: {
  sticker: CutSticker;
  sheets: ReadonlyArray<string>;
  /** a short handwritten word on the front, like a signature */
  note?: string;
}) {
  return (
    <span className="relative block aspect-[1.22] w-full">
      <span className="absolute top-[3%] left-0 h-[20%] w-[42%] rounded-t-[14px] bg-folder-back" />
      <span className="absolute inset-x-0 top-[11%] bottom-0 rounded-[18px] rounded-tl-none bg-folder-back" />
      {sheets.slice(0, 3).map((color, i) => (
        <span
          key={i}
          className="absolute h-[60%] w-[46%] rounded-[8px] transition-transform duration-500 ease-spring group-hover:-translate-y-[8%]"
          style={{
            left: `${11 + i * 17}%`,
            top: `${1 + i * 3}%`,
            backgroundColor: color,
            rotate: `${[-7, 3, 9][i]}deg`,
            transitionDelay: `${i * 40}ms`,
            boxShadow: '0 1px 2px rgb(0 0 0 / 0.08)',
          }}
        >
          <span className="absolute top-[16%] left-[14%] h-[5%] w-[58%] rounded-full bg-black/12" />
          <span className="absolute top-[30%] left-[14%] h-[5%] w-[40%] rounded-full bg-black/8" />
        </span>
      ))}
      <span className="absolute inset-x-0 top-[34%] bottom-0 rounded-[18px] border border-white/80 bg-white/55 shadow-[inset_0_1px_0_rgb(255_255_255/0.9)] backdrop-blur-[10px]" />
      <Sticker
        name={sticker}
        size={96}
        rotate={-6}
        className="absolute right-[5%] bottom-[5%] transition-transform duration-500 ease-spring group-hover:scale-105 group-hover:rotate-0"
        style={{ width: '40%', height: 'auto' }}
      />
      {note && (
        <span className="absolute bottom-[7%] left-[8%] text-sm font-semibold text-ink/35">
          {note}
        </span>
      )}
    </span>
  );
}

/** A subject's folder for homework: the glass folder, its name and how many tasks are left. */
export function FolderTile({
  title,
  subtitle,
  colors: c,
  sticker,
  alert,
  active = false,
  onClick,
}: {
  title: string;
  subtitle: string;
  colors: Swatch;
  sticker: CutSticker;
  alert?: boolean;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`group flex w-full flex-col gap-2 rounded-[22px] p-2 text-left transition-colors ${active ? 'bg-white' : 'hover:bg-white/50'}`}
    >
      <FolderArt sticker={sticker} sheets={['#ffffff', c.face, '#ffffff']} />
      <span className="flex items-center gap-2 px-1">
        <span className="min-w-0 flex-1 truncate font-display text-[15px] leading-5 font-bold text-ink">
          {title}
        </span>
        {alert && <span className="size-2.5 shrink-0 rounded-full bg-[#e5484d]" />}
      </span>
      <span className="-mt-2 px-1 text-xs font-medium text-muted">{subtitle}</span>
    </button>
  );
}

/** A push pin for a note: a glossy round head, flat on the paper (no shadow). */
export function Pin({ color = '#ff5a5f', className = '' }: { color?: string; className?: string }) {
  return (
    <span aria-hidden className={`pointer-events-none absolute size-[22px] ${className}`}>
      <span
        className="absolute inset-0 rounded-full"
        style={{
          backgroundColor: color,
          backgroundImage:
            'radial-gradient(circle at 34% 30%, rgb(255 255 255 / 0.85) 0 2.5px, rgb(255 255 255 / 0.25) 5px, transparent 9px), radial-gradient(circle at 60% 70%, rgb(0 0 0 / 0.18), transparent 70%)',
        }}
      />
    </span>
  );
}

/**
 * Two pages of a ring-bound notebook side by side, set as close as the cards around them, with
 * the rings across the gap (one page under the other on a phone). `split` is the left page's
 * share of the width.
 */
export function NotebookSpread({
  left,
  right,
  split = 0.5,
  className = '',
}: {
  left: ReactNode;
  right: ReactNode;
  split?: number;
  className?: string;
}) {
  return (
    <div
      className={`relative grid gap-2 lg:grid-cols-[var(--left)_minmax(0,1fr)] ${className}`}
      style={{ '--left': `calc(${split * 100}% - 4px)` } as CSSProperties}
    >
      {left}
      {right}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-10 hidden -translate-x-1/2 flex-col justify-around lg:flex"
        style={{ left: `${split * 100}%` }}
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="block h-[16px] w-11 rounded-full border-[3px] border-[#8a8f9c] bg-transparent"
          />
        ))}
      </div>
    </div>
  );
}
